import React, { useState } from 'react';
import { RSButton, RSInput, RSCheckbox, RSFormLabel, RSRadio } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import RadioGroup from '../RadioGroup';
import Styles from '../../styles/colors';
import AddMethodController from '../../merode/controllers/AddMethodController';
import { useController, useAsynchronousAction } from '../../hooks';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { MethodProperties, MethodProvenance, MethodType } from '../../merode/commands/types';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange, type RSCheckboxChange } from '../Inspector/rsForm';

/** A typed controller method, re-typed to the hook's loose action shape. */
type AsyncActionLike = (...args: unknown[]) => Promise<unknown>;

interface AddMethodFormProps {
  /** The ModelVersion. */
  modelVersion: IModelVersion;
  /** The id of the owner event type for the new method. */
  eventType: string;
  /** The id of the owner object type for the new method. */
  objectType: string;
  /** Callback to cancel adding the method. */
  onCancel?: () => void;
  /** Callback on generation of a report. */
  onReport?: (report: Report) => void;
}

/**
 * Form for adding a method type.
 *
 * Props:
 * - modelVersion: the ModelVersion
 * - eventType: the owner event type for the new method
 * - objectType: the owner object type for the new method
 * - onCancel: callback to cancel adding the method
 * - onReport: callback on generation of a report
 */
function AddMethodForm({ modelVersion, eventType, objectType, onCancel, onReport }: AddMethodFormProps) {
  // Store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only.
  const controller = useController(
    () =>
      new AddMethodController(
        modelVersion as unknown as IControllerModelVersion,
        eventType,
        objectType
      )
  );

  // These don't change for the life-time of the controller, so the class cached
  // them as instance fields in the constructor. Compute once and keep stable.
  const [acquirableMethods] = useState(() => controller.acquirableMethods);
  const [specialisableMethodsAll] = useState(() => controller.specialisableMethods);
  const [inheritableMethods] = useState(() => controller.inheritableMethods);

  // State (was constructor-initialised this.state).
  const [name, setName] = useState(() => controller.defaultName);
  const [type, setType] = useState<MethodType>('MODIFY');
  const [provenance, setProvenance] = useState<MethodProvenance>('OWNED');
  const [viaDependency, setViaDependency] = useState<IDependency | null>(null);
  const [viaMethod, setViaMethod] = useState<IMethod | null>(null);
  const [auto, setAuto] = useState(() => modelVersion.model.propagateOnMethod);

  // The async add flow. busy reproduces the `working` flag and onReport fires on
  // success — same timing as the class's setState({working:true}) -> await ->
  // setState({working:false}) -> onReport(report). The hook forwards args/result
  // untyped; cast the action and the resolved `Report`.
  const addAction = useAsynchronousAction(
    ((properties: MethodProperties, autoFlag: boolean) =>
      controller.addMethod(properties, autoFlag)) as AsyncActionLike,
    (report) => {
      if (onReport) {
        onReport(report as Report);
      }
    }
  );
  const working = addAction.busy;

  // Derived values (were getters on the class).
  const specialisableMethods = specialisableMethodsAll.filter((m) => m.type === type);

  const effectiveName =
    provenance === 'INHERITED' ? (viaMethod != null ? viaMethod.name : '') : name;
  const effectiveType =
    provenance === 'INHERITED' ? (viaMethod != null ? viaMethod.type : '') : type;

  // Validation (was the `valid` getter). Note: it checks `name` (the raw state),
  // not the derived effectiveName — preserved exactly.
  const nameCheck = controller.checkNameInput(name);
  const acquiredCheck =
    provenance !== 'ACQUIRED' || (viaDependency !== null && viaMethod !== null);
  const specialisedCheck = provenance !== 'SPECIALISED' || viaMethod !== null;
  const inheritedCheck = provenance !== 'INHERITED' || viaMethod !== null;
  const valid =
    name.length > 0 && nameCheck.ok && acquiredCheck && specialisedCheck && inheritedCheck;

  /**
   * Add the method
   */
  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (valid) {
      // `effectiveType` is the empty string only when `provenance === 'INHERITED'`
      // and no via-method is chosen; `valid` rules that out before we get here, so
      // it is a real `MethodType` at runtime. `viaDependency`/`viaMethod` are
      // likewise non-null in the branches that read `.id` (the `valid` checks
      // guarantee it). Casts are type-level only — runtime is unchanged.
      const properties: MethodProperties = {
        name: effectiveName,
        ownerEventType: eventType,
        ownerObjectType: objectType,
        type: effectiveType as MethodType,
        provenance,
      };
      if (provenance === 'ACQUIRED') {
        properties.viaDependency = viaDependency!.id;
        properties.viaMethod = viaMethod!.id;
      }
      if (provenance === 'SPECIALISED') {
        properties.viaMethod = viaMethod!.id;
      }
      if (provenance === 'INHERITED') {
        properties.viaMethod = viaMethod!.id;
        properties.viaInheritance = controller.inheritanceAsSubtype!.id;
      }
      addAction.execute(properties, auto);
    }
  };

  /**
   * Change handler for the provenance. Also clear the via method when the
   * provenance changes.
   */
  const onChangeProvenance = (value: string) => {
    setProvenance(value as MethodProvenance);
    setViaMethod(null);
  };

  /**
   * Change handler for the via dependency dropdown. Also clear the via method
   * when the via dependency actually changes.
   *
   * The class used a functional setState that returned {} (a no-op merge) when
   * the dependency was unchanged. We reproduce that by only clearing viaMethod
   * when the dependency differs from the latest value (read via the functional
   * updater so we never act on a stale closure).
   */
  const onChangeViaDependency = (change: MerlinSelectOption | null) => {
    const dep = change ? ((change as { dependency: IDependency }).dependency) : null;
    setViaDependency((current) => {
      if (current === dep) {
        // no change — leave viaMethod untouched (matches the {} no-op merge)
        return current;
      }
      setViaMethod(null);
      return dep;
    });
  };

  const onChangeViaMethod = (change: MerlinSelectOption | null) => {
    const method = change ? ((change as { method: IMethod }).method) : null;
    setViaMethod(method);
  };

  const renderButtons = () => {
    return (
      <div className="button-wrapper">
        <RSButton
          value="Cancel"
          type="button"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          onClick={onCancel}
        />
        <RSButton
          type="submit"
          value={rsValue(working ? null : 'Add')}
          disabled={!valid}
          iconName={rsValue(working ? 'FaSpinner' : null)}
          iconSize={15}
          className={rsValue(working ? 'spin' : null)}
        />
      </div>
    );
  };

  const renderViaDependency = () => {
    if (provenance === 'ACQUIRED') {
      const dependencies = Array.from(acquirableMethods.keys());
      return (
        <div className="rs-element rs-input-container rs-input via-dependency">
          <RSFormLabel>Via dependency: </RSFormLabel>
          <MerlinSelect
            name="viaDependency"
            options={dependencies.map((d) => ({
              value: d.id,
              label: d.displayName,
              dependency: d,
            }))}
            value={viaDependency ? viaDependency.id : null}
            onChange={onChangeViaDependency}
            clearable={false}
            placeholder=""
          />
        </div>
      );
    } else {
      return null;
    }
  };

  const renderViaMethod = () => {
    let methods: IMethod[] | undefined;
    let labelPrefix = 'Via';
    if (provenance === 'ACQUIRED') {
      // `viaDependency` may be null here; the Map returns `undefined` for a
      // missing key, which `|| []` handles, exactly as the original JS.
      methods = acquirableMethods.get(viaDependency as IDependency) || [];
    } else if (provenance === 'SPECIALISED') {
      methods = specialisableMethods || [];
      labelPrefix = 'Specialises';
    } else if (provenance === 'INHERITED') {
      methods = inheritableMethods || [];
      labelPrefix = 'Inherit';
    }
    return (
      methods != null && (
        <div className="rs-element rs-input-container rs-input via-method">
          <RSFormLabel>{labelPrefix} method: </RSFormLabel>
          <MerlinSelect
            name="viaMethod"
            options={methods.map((m) => ({
              value: m.id,
              label: m.asString(),
              method: m,
            }))}
            value={viaMethod ? viaMethod.id : null}
            onChange={onChangeViaMethod}
            clearable={false}
            placeholder=""
          />
        </div>
      )
    );
  };

  const renderForm = () => {
    return (
      <div className="modal-body">
        <div className="container">
          <div className="row">
            <div className="col-3">
              <RSInput
                autoFocus
                name="name"
                label="Method name"
                value={effectiveName}
                onChange={asRSChange<RSInputChange>((value: string) => setName(value))}
                disabled={working || provenance === 'INHERITED'}
                level={rsValue(nameCheck.ok ? null : 'danger')}
                text={rsValue(nameCheck.ok ? null : nameCheck.message)}
              />
            </div>
            <div className="col-3">
              <RSFormLabel>For object</RSFormLabel>
              <div>{controller.objectTypeName}</div>
            </div>
            <div className="col-3">
              <RSFormLabel>For event</RSFormLabel>
              <div>{controller.eventTypeName}</div>
            </div>
          </div>
          <div className="separator" />
          <RSCheckbox
            label="Automatically propagate method"
            checked={auto}
            onChange={asRSChange<RSCheckboxChange>(
              (value: string | number, checked: boolean) => setAuto(checked)
            )}
            disabled={working}
          />
          <div className="separator" />
          <RSFormLabel>Type</RSFormLabel>
          <RadioGroup
            selectedValue={effectiveType}
            name="radio-group"
            onChange={(value: string) => setType(value as MethodType)}
            disabled={provenance === 'INHERITED'}
          >
            <RSRadio value="CREATE" label="Create" disabled={provenance === 'INHERITED'} />
            <RSRadio value="MODIFY" label="Modify" disabled={provenance === 'INHERITED'} />
            <RSRadio value="END" label="End" disabled={provenance === 'INHERITED'} />
          </RadioGroup>
          <div className="separator" />
          <RSFormLabel>Provenance</RSFormLabel>
          <RadioGroup selectedValue={provenance} name="radio-group" onChange={onChangeProvenance}>
            <RSRadio value="OWNED" label="Owned method" />
            <RSRadio
              value="ACQUIRED"
              label="Acquired method"
              disabled={acquirableMethods.size === 0}
            />
            <RSRadio
              value="INHERITED"
              label="Inherited method"
              disabled={inheritableMethods.length === 0}
            />
            <RSRadio
              value="SPECIALISED"
              label="Specialised method"
              disabled={specialisableMethods.length === 0}
            />
          </RadioGroup>
          {renderViaDependency()}
          {renderViaMethod()}
        </div>
      </div>
    );
  };

  return (
    <div className="merlin-modal add-method-form">
      <div className="title">Add Method</div>
      <form className="form" onSubmit={onAdd}>
        {renderForm()}
        {renderButtons()}
      </form>
    </div>
  );
}

export default AddMethodForm;
